/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Pagination,
} from "@mui/material"
import React, { useEffect, useState } from "react"

import { DateTime } from "luxon"
import {
  and,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore"
import { auth, db, functions } from "../../firebase"
import { PopupService } from "react-ui-components/components/PopupService"
import { Formik } from "formik"
import { IRaffle } from "react-ui-components/components/models"
import dayjs from "dayjs"
import { httpsCallable } from "firebase/functions"
import htmr from "htmr"

export const sendTicketsToUsers = httpsCallable(functions, "sendTicketsToUsers")

export const mapUserTicketDocument = (
  docSnap: { id: string; data: () => Record<string, any> },
): Record<string, any> & { id: string } => ({
  id: docSnap.id,
  ...docSnap.data(),
})

export const isActiveAdminUserTicket = (ticket: any, now = Date.now()) =>
  Boolean(
    ticket &&
      !ticket.disabled &&
      (typeof ticket.expirationDate !== "number" || ticket.expirationDate >= now),
  )

export const getRaffleTicketDocumentId = (ticket: { id?: string; raffleTicketId?: string }) =>
  ticket.raffleTicketId || ticket.id || ""

/**
 * UserTickets component fetches and displays user tickets and available raffles.
 *
 * @param {Object} props - Component props.
 * @param {string} props.userId - The ID of the user whose tickets are being fetched.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @component
 *
 * @example
 * return (
 *   <UserTickets userId="user123" />
 * )
 *
 * @remarks
 * This component uses Firebase Firestore to fetch user tickets and available raffles.
 * It uses Formik for form handling and validation.
 *
 * @requires
 * - `db` - Firestore database instance.
 * - `IRaffle` - Interface for raffle data.
 * - `sendTicketsToUsers` - Function to send tickets to users.
 * - `dayjs` - Library for date manipulation.
 *
 * @see {@link https://formik.org/|Formik}
 * @see {@link https://day.js.org/|dayjs}
 */
const UserTickets = ({ userId }: { userId: string }) => {
  const [tickets, setTickets] = useState<any>([])
  const [raffles, setRaffles] = useState<IRaffle[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [page, setPage] = useState<number>(1)
  const [totalTickets, setTotalTickets] = useState<number>(0)
  const [disabledTickets, setDisabledTickets] = useState<number>(0)
  const [expiredTickets, setExpiredTickets] = useState<number>(0)
  const ticketsPerPage = 10

  const fetchTickets = async () => {
    try {
      setLoading(true)
      const ticketsCollection = collection(db, `users/${userId}/tickets`)
      const ticketSnapshot = await getDocs(ticketsCollection)
      const ticketList = ticketSnapshot.docs.map(mapUserTicketDocument)
      const activeTickets = ticketList.filter((ticket) => isActiveAdminUserTicket(ticket))
      activeTickets.sort((a, b) => b.createdDate - a.createdDate)
      setTickets(activeTickets)
      setTotalTickets(activeTickets.length)
      setDisabledTickets(ticketList.filter((ticket) => ticket.disabled).length)
      setExpiredTickets(ticketList.filter((ticket) => ticket.expirationDate < Date.now()).length)
    } catch (error) {
      console.error("Error fetching tickets: ", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchRaffles = async () => {
    try {
      setLoading(true)
      const col = query(
        collection(db, `raffles`),
        and(
          where("disabled", "==", false),
          where("raffleDate", ">=", new Date().getMilliseconds()),
        ),
      )
      const snapshot = await getDocs(col)
      const list: IRaffle[] = snapshot.docs.map((doc) => doc.data() as IRaffle)
      setRaffles(list)
    } catch (error) {
      console.error("Error fetching raffles: ", error)
    } finally {
      setLoading(false)
    }
  }

  const deleteTicketsForRaffle = async (raffleId: string) => {
    try {
      setLoading(true)

      const userTicketsQuery = query(
        collection(db, `users/${userId}/tickets`),
        where("raffleId", "==", raffleId),
      )
      const userTicketsSnapshot = await getDocs(userTicketsQuery)

      const raffleTicketsQuery = query(
        collection(db, `raffles/${raffleId}/tickets`),
        where("payerUID", "==", userId),
      )
      const raffleTicketsSnapshot = await getDocs(raffleTicketsQuery)

      const deletePromises = [
        ...userTicketsSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)),
        ...raffleTicketsSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)),
      ]

      await Promise.all(deletePromises)

      PopupService.pushPopup(
        `Deleted ${userTicketsSnapshot.size} user tickets and ${raffleTicketsSnapshot.size} raffle tickets.`,
      )
    } catch (error) {
      console.error("Error deleting tickets: ", error)
      PopupService.pushPopup("Failed to delete tickets.")
    } finally {
      setLoading(false)
      fetchTickets()
    }
  }

  const deleteRaffleLikeForUser = async (raffleId: string) => {
    try {
      setLoading(true)

      const likeRef = doc(db, `raffles/${raffleId}/likes/${userId}`)
      const likeSnapshot = await getDoc(likeRef)

      if (!likeSnapshot.exists()) {
        PopupService.pushPopup("No raffle like found for this user.")
        return
      }

      await deleteDoc(likeRef)
      PopupService.pushPopup("Raffle like removed for this user.")
    } catch (error) {
      console.error("Error removing raffle like: ", error)
      PopupService.pushPopup("Failed to remove raffle like.")
    } finally {
      setLoading(false)
    }
  }

  const deleteTicket = async (ticket: { id?: string; raffleId?: string; raffleTicketId?: string }) => {
    if (!ticket?.id || !ticket?.raffleId) {
      PopupService.pushPopup("Ticket is missing required raffle data.")
      return
    }

    const raffleTicketId = getRaffleTicketDocumentId(ticket)

    try {
      setLoading(true)
      await Promise.all([
        deleteDoc(doc(db, "users", userId, "tickets", ticket.id)),
        deleteDoc(doc(db, "raffles", ticket.raffleId, "tickets", raffleTicketId)),
      ])
      PopupService.pushPopup("Ticket deleted successfully!")
      fetchTickets()
    } catch (error) {
      console.error("Error deleting ticket: ", error)
      PopupService.pushPopup("Failed to delete ticket.")
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteTicketsForRaffle = (raffleId: string) => {
    if (!raffleId) {
      PopupService.pushPopup("Select a raffle to delete tickets.")
      return
    }

    PopupService.setConfirm(
      "Delete all tickets for this user in the selected raffle? This will remove them from the user and raffle collections.",
      (result?: string) => {
        if (result !== "OK") {
          return
        }

        void deleteTicketsForRaffle(raffleId)
      },
    )
  }

  const handleDeleteRaffleLikeForRaffle = (raffleId: string) => {
    if (!raffleId) {
      PopupService.pushPopup("Select a raffle to remove likes.")
      return
    }

    PopupService.setConfirm(
      "Remove the user's like for the selected raffle?",
      (result?: string) => {
        if (result !== "OK") {
          return
        }

        void deleteRaffleLikeForUser(raffleId)
      },
    )
  }

  useEffect(() => {
    fetchRaffles()
    fetchTickets()
  }, [userId])

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const paginatedTickets = tickets.slice((page - 1) * ticketsPerPage, page * ticketsPerPage)

  return (
    <Box className="flex flex-col gap-5 items-center justify-center h-screen">
      <Typography variant="h6">User ID: {userId}</Typography>
      <Box sx={{ minWidth: 120, marginTop: 2 }}>
        <Formik
          initialValues={{ raffleId: "", ticketsCount: "" }}
          validate={(values) => {
            const errors: any = {}
            if (!values.raffleId) {
              errors.raffleId = "Required"
            } else if (
              !values.ticketsCount ||
              isNaN(Number(values.ticketsCount)) ||
              Number(values.ticketsCount) <= 0
            ) {
              errors.ticketsCount = "Invalid Tickets Count"
            }
            return errors
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              setLoading(true)
              const response = await sendTicketsToUsers({
                ...values,
                userId,
              })
              console.log("Response: ", response)
              PopupService.pushPopup("Tickets sent successfully!")
            } catch (error) {
              console.error("Error sending tickets: ", error)
              PopupService.pushPopup("Failed to send tickets.")
            } finally {
              setSubmitting(false)
              setLoading(false)
              fetchTickets()
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <FormControl fullWidth className="mt-5">
                <InputLabel id="raffles-selector-label">{"Select Raffle"}</InputLabel>
                <Select
                  className="flex flex-column gap-10"
                  label={"Select Raffle"}
                  labelId="raffles-selector-label"
                  name="raffleId"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.raffleId}
                >
                  {raffles?.map((item, i) => (
                    <MenuItem key={i} value={item.id} className="gap-2">
                      <Typography color="error">
                        {!item.raffleDate ? "No Date" : dayjs(item.raffleDate).format("MM/DD/YYYY")}
                      </Typography>
                      <Typography color="success">{item.name}</Typography>
                      <Typography color="secondary">{item.id}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.raffleId && touched.raffleId && (
                <Typography color="error">{errors.raffleId}</Typography>
              )}

              <FormControl fullWidth className="mt-5">
                <InputLabel>Tickets Count</InputLabel>
                <Input
                  type="number"
                  name="ticketsCount"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.ticketsCount}
                ></Input>
              </FormControl>
              {errors.ticketsCount && touched.ticketsCount && (
                <Typography color="error">{errors.ticketsCount}</Typography>
              )}

              <Button
                fullWidth
                className="mt-5"
                variant="outlined"
                color="success"
                disabled={isSubmitting || !!errors.raffleId || !!errors.ticketsCount}
                type="submit"
              >
                Submit
              </Button>
              <Button
                fullWidth
                className="mt-3"
                variant="outlined"
                color="error"
                type="button"
                disabled={isSubmitting || loading || !values.raffleId}
                onClick={() => handleDeleteTicketsForRaffle(values.raffleId)}
              >
                Delete Tickets for Selected Raffle
              </Button>
              <Button
                fullWidth
                className="mt-3"
                variant="outlined"
                color="warning"
                type="button"
                disabled={isSubmitting || loading || !values.raffleId}
                onClick={() => handleDeleteRaffleLikeForRaffle(values.raffleId)}
              >
                Remove Raffle Like for Selected Raffle
              </Button>
            </form>
          )}
        </Formik>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Box className="flex flex-row gap-5 items-center justify-center w-full">
            <Typography>Total Tickets: {totalTickets}</Typography>
            <Typography>Disabled Tickets: {disabledTickets}</Typography>
            <Typography>Expired Tickets: {expiredTickets}</Typography>
          </Box>
          <Box className="flex flex-row flex-wrap gap-5 items-center justify-center w-50 h-50 overflow-auto">
            {paginatedTickets?.map(
              (
                item: {
                  id: string
                  isWinner: boolean
                  code:
                    | string
                    | number
                    | boolean
                    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
                    | Iterable<React.ReactNode>
                    | null
                    | undefined
                  payerUID: any
                  expirationDate: number
                  createdDate: number
                  createdBy: string
                  raffleType: string
                  raffleId?: string
                  raffleTicketId?: string
                },
                i: number,
              ) => {
                return (
                  <Box key={i}>
                    <Card
                      sx={{ mx: "auto", mt: 4 }}
                      style={{
                        backgroundColor: item.isWinner ? "green" : "transparent",
                        borderWidth: 1,
                      }}
                    >
                      <CardContent>
                        <Box className="flex flex-col gap-1">
                          <Typography gutterBottom variant="h6" component="div">
                            {item.code}
                          </Typography>
                          <Typography>
                            Created Date: {dayjs(item.createdDate).format("MM/DD/YYYY")}
                          </Typography>
                          <Typography>Created By: {item.createdBy}</Typography>
                          <Typography>
                            Expiration Date: {dayjs(item.expirationDate).format("MM/DD/YYYY")}
                          </Typography>
                          <Typography>
                            Raffle Type: {item.raffleType && htmr(item.raffleType)}
                          </Typography>
                          <Box className="flex flex-row gap-3">
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => deleteTicket(item)}
                            >
                              Delete
                            </Button>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={async () => {
                                try {
                                  setLoading(true)
                                  await updateDoc(doc(db, "users", userId, "tickets", item.id), {
                                    disabled: true,
                                  })
                                  const raffleTicketId = getRaffleTicketDocumentId(item)
                                  if (item.raffleId && raffleTicketId) {
                                    await updateDoc(
                                      doc(db, "raffles", item.raffleId, "tickets", raffleTicketId),
                                      {
                                        disabled: true,
                                      },
                                    )
                                  }
                                  PopupService.pushPopup("Ticket disabled successfully!")
                                  fetchTickets()
                                } catch (error) {
                                  console.error("Error disabling ticket: ", error)
                                  PopupService.pushPopup("Failed to disable ticket.")
                                } finally {
                                  setLoading(false)
                                }
                              }}
                            >
                              Disable
                            </Button>
                            <Button
                              variant="outlined"
                              color="success"
                              onClick={async () => {
                                try {
                                  setLoading(true)
                                  await sendTicketsToUsers({
                                    userId,
                                    ticketId: item.id,
                                  })
                                  PopupService.pushPopup("Ticket sent successfully!")
                                  fetchTickets()
                                } catch (error) {
                                  console.error("Error sending ticket: ", error)
                                  PopupService.pushPopup("Failed to send ticket.")
                                } finally {
                                  setLoading(false)
                                }
                              }}
                            >
                              Send
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                )
              },
            )}
          </Box>
          <Pagination
            count={Math.ceil(tickets.length / ticketsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            className="mt-5"
          />
        </>
      )}
    </Box>
  )
}

export default UserTickets
